// Wave 20.7 — Public peak-drop landing page.
//
// Route: /peak/:token. No auth. Renders one peak stat + the rider's
// display name + a "Beat this" CTA. If the token is expired or
// unknown, shows a friendly "This peak has expired" page.
//
// The whole point is virality — no signup wall, no header noise.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import SharedCelebration from './components/SharedCelebration';
import DropInteractions from './components/DropInteractions';
import ExpirationCountdown from './components/ExpirationCountdown';
import FollowButton from './components/FollowButton';
import { colors } from './theme';
import {
  fmtDistance,
  fmtPeakDistance,
  fmtPace,
  fmtSpeed,
  useUnitSystem,
  type UnitSystem,
} from './services/useUnitSystem';

interface PeakPayload {
  peak: {
    kind: string;
    value: number;
    unit: string;
    contextLabel: string | null;
    // Wave 20.31 — effort window and creator's unit preference.
    durationSec: number | null;
    distanceM: number | null;
    displayUnits: 'metric' | 'imperial' | null;
    createdAt: string;
  };
  rider: { id: number; username: string | null; displayName: string };
  expiresAt: string | null;
  // Wave 20.49am — backend echoes whether the request came
  // from the drop owner. Powers the "Drop again" affordance on
  // expired drops.
  isOwner: boolean;
}

interface LeaderboardRow {
  sessionId: string;
  recordedAt: string;
  actor: { id: number; username: string | null; displayName: string };
  peakValue: number | null;
  peakUnit: string;
  peakDurationSec: number | null;
  peakDistanceM: number | null;
  activityType: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';

const PRETTY_TARGETS: Record<string, string> = {
  '5s': '5s', '30s': '30s',
  '1min': '1min', '5min': '5min', '20min': '20min', '60min': '60min',
  '1mi': '1mi', '5k': '5K', '10k': '10K', '40k': '40K',
  'half': 'Half Marathon', 'full': 'Marathon',
};
function kindLabel(kind: string): string {
  // Wave 20.46 — per-metric peaks.
  if (kind === 'peak_whole') return 'Entire session';
  if (kind === 'peak_pace') return 'Peak Pace';
  if (kind === 'peak_speed') return 'Peak Speed';
  if (kind === 'peak_power') return 'Peak Power';
  // Wave 20.49ag — safer to default to Grade-Adj Speed for
  // peak-drop pages since the majority of drops come from
  // cycling activities. Running-specific peaks (peak_pace*)
  // still render as "Peak Pace" via the runs above.
  if (kind === 'peak_gap') return 'Peak Grade-Adj Speed';
  if (kind === 'peak_vam') return 'Peak Climb (VAM)';
  if (kind === 'peak_grade_adj_speed') return 'Peak Speed';
  if (kind === 'peak_grade_adj_pace') return 'Peak Pace';
  // Wave 20.27 — Session-total drop kinds.
  if (kind === 'session_distance' || kind === 'summary_distance') return 'Total Distance';
  if (kind === 'session_elevation_gain' || kind === 'summary_elevation_gain') return 'Total Elevation';
  if (kind === 'session_duration' || kind === 'summary_duration') return 'Total Time';
  if (kind === 'session_avg_pace' || kind === 'summary_avg_pace') return 'Avg Pace';
  if (kind === 'session_best_pace' || kind === 'summary_best_pace') return 'Best Pace';
  if (kind === 'session_best_peak' || kind === 'summary_best_peak') return 'Best Peak';
  if (kind === 'session_avg_speed' || kind === 'summary_avg_speed') return 'Avg Speed';
  if (kind === 'session_max_speed' || kind === 'summary_max_speed') return 'Max Speed';
  const parts = kind.split('_');
  const t = (p: string | undefined): string =>
    p ? (PRETTY_TARGETS[p] ?? p) : '';
  if (parts[0] === 'peak' && parts[1] === 'power' && parts[2]) return `Best ${t(parts[2])} Power`;
  if (parts[0] === 'peak' && parts[1] === 'speed' && parts[2]) return `Fastest ${t(parts[2])}`;
  if (parts[0] === 'peak' && parts[1] === 'pace' && parts[2]) return `Fastest ${t(parts[2])}`;
  if (parts[0] === 'peak' && parts[1] === 'gap') return `Best ${t(parts[2])} Grade-Adj Pace`;
  if (kind === 'effort_zone4_max_sec') return 'Longest Zone 4+ Effort';
  return kind;
}

function LeaderboardRow({
  row,
  rank,
  droppedValue,
  droppedKind,
  displayUnits,
}: {
  row: LeaderboardRow;
  rank: number;
  droppedValue: number;
  droppedKind: string;
  displayUnits: UnitSystem;
}) {
  const beat = row.peakValue != null && row.peakValue > droppedValue;
  const surface =
    row.activityType === 'outdoor_running'
      ? 'Run'
      : row.activityType === 'outdoor_cycling'
      ? 'Ride'
      : 'Aero';
  const link = row.actor.username ? `/u/${row.actor.username}` : undefined;
  return (
    <Box
      component={link ? 'a' : 'div'}
      href={link}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${beat ? colors.brand : colors.border}`,
        backgroundColor: colors.surface,
        textDecoration: 'none',
        color: 'inherit',
        cursor: link ? 'pointer' : 'default',
        '&:hover': link ? { borderColor: colors.brand } : {},
      }}
    >
      <Box
        sx={{
          minWidth: 28,
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 800,
          color: colors.textMuted,
          textAlign: 'right',
        }}
      >
        {rank}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: colors.text }}>
          {row.actor.displayName}
          {row.actor.username && (
            <Box component="span" sx={{ color: colors.textMuted, fontSize: '0.8rem', ml: 1 }}>
              @{row.actor.username}
            </Box>
          )}
        </Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
          {new Date(row.recordedAt).toLocaleDateString()}
          {row.peakDurationSec != null && ` · ${row.peakDurationSec}s`}
          {row.peakDistanceM != null && row.peakDistanceM > 0 && ` · ${fmtPeakDistance(row.peakDistanceM, displayUnits)}`}
          {row.activityType !== 'outdoor_cycling' && droppedKind === 'peak_power' && ` · ${surface}`}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 900,
          fontSize: '1.1rem',
          color: beat ? colors.brand : colors.text,
        }}
      >
        {row.peakValue != null
          ? formatValue(
              row.peakValue,
              row.peakUnit,
              displayUnits,
              row.activityType === 'outdoor_running',
            )
          : '—'}
      </Typography>
    </Box>
  );
}

// Wave 20.49ac — running-only pace kinds. Everything else with
// unit 'sec_per_m' renders as speed per rider preference
// ("cycling should be mph/kph, running should be min/mi"). Peak
// drop payload doesn't carry activityType so this kind-based
// heuristic disambiguates: peak_pace* + session_*_pace +
// summary_*_pace are all running; anything else with sec_per_m
// (peak_gap on a cycling drop, for instance) formats as speed.
function isRunPaceKindForDrop(kind: string): boolean {
  if (kind === 'peak_pace' || kind.startsWith('peak_pace_')) return true;
  if (kind === 'session_avg_pace' || kind === 'summary_avg_pace') return true;
  if (kind === 'session_best_pace' || kind === 'summary_best_pace') return true;
  return false;
}

function formatValue(
  value: number,
  unit: string,
  units: UnitSystem,
  isRun: boolean,
): string {
  switch (unit) {
    case 'W':
      return `${Math.round(value).toLocaleString()} W`;
    case 'mps':
      return fmtSpeed(value, units);
    case 'sec_per_m': {
      const mps = value > 0 ? 1 / value : 0;
      return isRun ? fmtPace(mps, units) : fmtSpeed(mps, units);
    }
    case 'sec':
      return `${value}s`;
    case 'm':
      return fmtDistance(value, units);
    case 'm_per_h':
      return units === 'imperial'
        ? `${Math.round(value * 3.28084).toLocaleString()} ft/h`
        : `${Math.round(value).toLocaleString()} m/h`;
    default:
      return `${value}`;
  }
}

function formatWindow(
  durationSec: number | null,
  distanceM: number | null,
  units: UnitSystem,
): string | null {
  const parts: string[] = [];
  if (durationSec != null && durationSec > 0) {
    if (durationSec < 60) parts.push(`${durationSec}s`);
    else {
      const m = Math.floor(durationSec / 60);
      const s = durationSec % 60;
      parts.push(s === 0 ? `${m}m` : `${m}m ${s}s`);
    }
  }
  if (distanceM != null && distanceM > 0) {
    parts.push(fmtPeakDistance(distanceM, units));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export default function PeakDropPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PeakPayload | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const viewerUnits = useUnitSystem();
  // Prefer the creator's units (stored at drop time) so the recipient
  // sees the same string the creator saw. Fall back to the viewer's
  // own preference for drops written before this column existed.
  const displayUnits: UnitSystem =
    data?.peak.displayUnits === 'imperial' || data?.peak.displayUnits === 'metric'
      ? data.peak.displayUnits
      : viewerUnits;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const jwt = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token');
    const headers = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
    fetch(`${API}/public/peak/${encodeURIComponent(token)}`, { headers })
      .then(async (r) => {
        if (r.status === 401) throw new Error('signin_required');
        if (r.status === 403) throw new Error('not_a_follower');
        if (r.status === 404) throw new Error('expired');
        if (!r.ok) throw new Error('unknown');
        return (await r.json()) as PeakPayload;
      })
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || 'unknown');
          setLoading(false);
        }
      });
    // Wave 20.25 — parallel leaderboard fetch. Non-fatal if it fails
    // — the peak still renders and we just skip the leaderboard block.
    fetch(`${API}/public/peak/${encodeURIComponent(token)}/leaderboard`, { headers })
      .then((r) => (r.ok ? r.json() : { attempts: [] }))
      .then((d) => {
        if (!cancelled) setLeaderboard(d?.attempts ?? []);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Wave 20.25 — Challenge CTA. On a mobile device we try the
  // aerochat:// scheme first (opens the app if installed) and fall
  // back to /drops after a short delay if nothing happened. Desktop
  // just goes to /drops with an "install the app" pitch.
  const startChallenge = () => {
    if (!token) return;
    const ua = navigator.userAgent || '';
    const onMobile = /iPhone|iPad|iPod|Android/i.test(ua);
    if (onMobile) {
      const scheme = `aerochat://challenge/${encodeURIComponent(token)}`;
      window.location.href = scheme;
      // If the app didn't take over, drop them at /drops after 800ms.
      setTimeout(() => {
        if (!document.hidden) window.location.href = `/drops?challenge=${token}`;
      }, 800);
    } else {
      window.location.href = `/drops?challenge=${token}`;
    }
  };

  // Wave 20.49am — Owner can re-mint an expired peak drop.
  // Uses the existing POST /peaks/share endpoint with the same
  // peak data + a fresh default window; on success we navigate
  // to the new drop URL so the owner keeps browsing.
  const [redropping, setRedropping] = useState(false);
  const [redropError, setRedropError] = useState<string | null>(null);
  const redropPeak = async () => {
    if (!data) return;
    setRedropError(null);
    setRedropping(true);
    try {
      const jwt = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token');
      if (!jwt) {
        setRedropError('Sign in to drop again.');
        setRedropping(false);
        return;
      }
      const res = await fetch(`${API}/peaks/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          kind: data.peak.kind,
          value: data.peak.value,
          unit: data.peak.unit,
          contextLabel: data.peak.contextLabel,
          durationSec: data.peak.durationSec,
          distanceM: data.peak.distanceM,
          displayUnits: data.peak.displayUnits,
          window: '24h',
          visibility: 'public',
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { token: string };
      window.location.href = `/dropped/${body.token}`;
    } catch (e: any) {
      setRedropError(e?.message ? `Couldn't drop again (${e.message})` : "Couldn't drop again");
      setRedropping(false);
    }
  };

  if (loading) {
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }

  if (error || !data) {
    const isSignin = error === 'signin_required';
    const isFollow = error === 'not_a_follower';
    return (
      <PageLayout maxWidth="sm">
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {isSignin
              ? 'Sign in to view this peak'
              : isFollow
              ? 'Followers only'
              : error === 'expired'
              ? 'This peak has expired'
              : 'Peak not found'}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 4 }}>
            {isSignin
              ? 'The sender restricted this peak to their followers. Sign in to continue.'
              : isFollow
              ? "You're signed in, but only the sender's followers can view this peak."
              : error === 'expired'
              ? 'The sender set a time limit that has passed.'
              : "The link may be mistyped, or it was never valid."}
          </Typography>
          <Button
            variant="contained"
            href={isSignin ? `/login?next=/peak/${token}` : '/'}
            sx={{ backgroundColor: colors.brand, color: colors.textInverse }}
          >
            {isSignin ? 'Sign in' : 'Go to aero.chat'}
          </Button>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="sm">
      <SharedCelebration />
      <Stack spacing={4} sx={{ pt: 6, pb: 8, alignItems: 'center', textAlign: 'center' }}>
        <Chip
          label="LIVE PEAK"
          size="small"
          sx={{
            backgroundColor: colors.brand,
            color: colors.textInverse,
            fontWeight: 800,
            letterSpacing: '0.15em',
          }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
          <Typography variant="h5" sx={{ color: colors.textMuted, fontWeight: 600 }}>
            {data.rider.displayName} just dropped
          </Typography>
          <FollowButton userId={data.rider.id} compact />
        </Stack>
        <Box
          sx={{
            width: '100%',
            p: { xs: 4, sm: 6 },
            borderRadius: 4,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            boxShadow: `0 0 60px ${colors.brand}22`,
          }}
        >
          <Typography
            sx={{
              color: colors.brand,
              fontSize: '0.85rem',
              fontWeight: 800,
              letterSpacing: '0.15em',
              mb: 1,
            }}
          >
            {kindLabel(data.peak.kind).toUpperCase()}
          </Typography>
          <Typography
            sx={{
              color: colors.text,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: { xs: '3.5rem', sm: '5rem' },
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.03em',
            }}
          >
            {formatValue(
              data.peak.value,
              data.peak.unit,
              displayUnits,
              isRunPaceKindForDrop(data.peak.kind),
            )}
          </Typography>
          {(() => {
            const window = formatWindow(data.peak.durationSec, data.peak.distanceM, displayUnits);
            return window ? (
              <Typography sx={{ color: colors.brand, mt: 1.5, fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.1em' }}>
                {window.toUpperCase()}
              </Typography>
            ) : null;
          })()}
          {data.peak.contextLabel && (
            <Typography sx={{ color: colors.textMuted, mt: 1, fontSize: '0.9rem' }}>
              {data.peak.contextLabel} · {new Date(data.peak.createdAt).toLocaleString()}
            </Typography>
          )}
          {(data.peak.durationSec != null || data.peak.distanceM != null) && (
            <Typography sx={{ color: colors.textMuted, mt: 1.5, fontSize: '0.8rem' }}>
              To beat this, hit a higher value over the same
              {data.peak.durationSec != null && data.peak.distanceM == null ? ' duration' : ''}
              {data.peak.distanceM != null && data.peak.durationSec == null ? ' distance' : ''}
              {data.peak.durationSec != null && data.peak.distanceM != null ? ' effort window' : ''}.
            </Typography>
          )}
        </Box>

        {/* Wave 20.49am — Owner-of-expired-drop path: the
            challenge CTA is dead (viewers 404), so replace it
            with "Drop again" that mints a fresh copy with a
            new expiration. Existing cheers + comments +
            leaderboard on the current expired token stay
            visible (backend now returns them to the owner). */}
        {data.isOwner && data.expiresAt && new Date(data.expiresAt).getTime() < Date.now() ? (
          <>
            <Typography variant="h4" sx={{ fontWeight: 900, mt: 2 }}>
              This drop expired
            </Typography>
            <Typography sx={{ color: colors.textMuted, maxWidth: 420 }}>
              Only you can see this now — the cheers, comments, and leaderboard
              below stay preserved. Mint a fresh one to keep sharing.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                onClick={redropPeak}
                disabled={redropping}
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: colors.brand,
                  color: colors.textInverse,
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                }}
              >
                {redropping ? 'Dropping…' : 'Drop again →'}
              </Button>
              <Button href="/feed" variant="text" sx={{ color: colors.textMuted }}>
                Back to feed
              </Button>
            </Stack>
            {redropError && (
              <Typography sx={{ color: colors.danger, fontSize: '0.8rem' }}>
                {redropError}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="h4" sx={{ fontWeight: 900, mt: 2 }}>
              Think you can beat it?
            </Typography>
            <Typography sx={{ color: colors.textMuted, maxWidth: 420 }}>
              Get the aero.chat mobile app, record your own ride, and drop them a peak that beats this one.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                onClick={startChallenge}
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: colors.brand,
                  color: colors.textInverse,
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                }}
              >
                {/* Wave 20.49al — Metric-agnostic wording. */}
                {data.expiresAt ? (
                  <>
                    You have <ExpirationCountdown expiresAt={data.expiresAt} showExpired={false} /> to challenge this
                  </>
                ) : (
                  <>Challenge this →</>
                )}
              </Button>
              <Button
                href="/"
                variant="text"
                sx={{ color: colors.textMuted }}
              >
                About aero.chat
              </Button>
            </Stack>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: -1 }}>
              Recording happens in the mobile app. Your attempt lands on this
              leaderboard when you save the session.
            </Typography>
          </>
        )}

        {leaderboard.length > 0 && (
          <Box sx={{ width: '100%', textAlign: 'left', mt: 4 }}>
            <Typography
              sx={{
                color: colors.brand,
                fontSize: '0.75rem',
                fontWeight: 800,
                letterSpacing: '0.2em',
                mb: 1.5,
              }}
            >
              LEADERBOARD · {leaderboard.length}
            </Typography>
            <Stack spacing={1}>
              {leaderboard.map((row, idx) => (
                <LeaderboardRow
                  key={row.sessionId}
                  row={row}
                  rank={idx + 1}
                  droppedValue={data.peak.value}
                  droppedKind={data.peak.kind}
                  displayUnits={displayUnits}
                />
              ))}
            </Stack>
          </Box>
        )}

        {data.expiresAt && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', mt: 3 }}>
            Drop Link expires in{' '}
            <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.brand, fontWeight: 700 }}>
              <ExpirationCountdown expiresAt={data.expiresAt} />
            </Box>
          </Typography>
        )}

        {/* Wave 20.10 — Reactions + comments */}
        <Box sx={{ width: '100%', textAlign: 'left' }}>
          <DropInteractions
            dropType="peak"
            token={token!}
            expiresAt={data.expiresAt}
          />
        </Box>
      </Stack>
    </PageLayout>
  );
}
